import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {RecordUtils} from "../PA.Core/Basic/Records";
import {
    IMessageProvider,
    IProgressProvider, MessageType
} from "../PA.Core/PA.Core.IF.Basic";
import {
    IBusinessObjectCreateLoadOptions,
    IBusinessObject,
    IBusinessObjectChanges,
    IBusinessObjectChangesOptions
} from "../PA.Core/PA.Core.IF.BO";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_MasterData_Set,
    esWB_Policy_Cube_Process,
    esWB_SalesOrg
} from "../PA.Core/PAdbSchema";
import {
    WB_MasterDataSetRow,
    WB_PolicyCubeProcessRow,
    WB_SalesOrgRow
} from "../PA.Core/PAdbSchemaRows";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {Role_User_Admin} from "../PA.Core/Security/Roles";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";
import {ErrorMessage_restate} from "../PA.Core/Basic/MessageUtils";

export class PolicyCubeProcessBO
    implements
        IBusinessObject
{

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_PolicyCubeProcess: WB_PolicyCubeProcessRow;


    constructor(options?: IBusinessObjectCreateLoadOptions) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Cube Process'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_PolicyCubeProcess = null;


    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_PolicyCubeProcess(): WB_PolicyCubeProcessRow {
        return this.drWB_PolicyCubeProcess;
    }
    
    async LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Cube Process");
        let olc = self._OnLoadComplete = (async () => {
            self.LoadOptions = options || {};

            try {
                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for new Cube Process.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                let newCubeProcessRow  = <WB_PolicyCubeProcessRow> await this.dbContext.createRecordWithKey(esWB_Policy_Cube_Process)
                newCubeProcessRow.setDefaults();
                self.drWB_PolicyCubeProcess = newCubeProcessRow;
                self.dbContext.add(esWB_Policy_Cube_Process, newCubeProcessRow);
                await self.applyCreateOptions()
                self.updateIdentity();


                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;

                self._OnLoadComplete = undefined;
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }

        })();
        return olc;
    }

    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = 'Customer Type already loaded.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }
                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for Cube Process.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = PolicyCubeProcessBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = PolicyCubeProcessBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                const aCubeProcessRow: any = recordResolver.get(esWB_Policy_Cube_Process, anId);

                if (_.isEmpty(aCubeProcessRow)) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception('Id not found.');
                    return self;
                }

                self.drWB_PolicyCubeProcess = aCubeProcessRow;
                self.updateIdentity();

                if (_.isNumber(self.drWB_PolicyCubeProcess.cif.WB_MDSET_ID) && self.drWB_PolicyCubeProcess.cif.WB_MDSET_ID > 0) {
                    let mruService = new MDUsageService()
                    await mruService.saveMDSetUsage(self.drWB_PolicyCubeProcess.cif.WB_MDSET_ID)
                }

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return olc;
    }

    private updateIdentity(): void {

        const {
            WB_POLICYCUBEPROCESS_ID,
            PROCESS_ID,
            PROCESS_NAME
        } = this.WB_PolicyCubeProcess.cif;
        this.Identity.setAll(WB_POLICYCUBEPROCESS_ID, PROCESS_ID, PROCESS_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.WB_PolicyCubeProcess, 'WB_POLICYCUBEPROCESS_ID', 'PROCESS_ID', 'PROCESS_NAME');
        let userContext = PAEnvironment.getValue(PAEnvironment.UserContext, 'UserContext')
        let bHasRole = userContext.hasRoleId(Role_User_Admin)

        if (bHasRole)
            this.Identity.enableRecordWriteBack(this.WB_PolicyCubeProcess, 'PROCESS_ID', 'PROCESS_NAME')
        else
            this.Identity.enableRecordWriteBack(this.WB_PolicyCubeProcess, undefined, undefined)

    }

    async applyCreateOptions() {
        const self = this
        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_PolicyCubeProcess.cif.PROCESS_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_PolicyCubeProcess.WB_MDSetRow = mds
                self.WB_PolicyCubeProcess.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Sales Org
        let wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
        if (_.isNumber(wbSalesOrg)) {
            let salesOrg: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg)
            if (salesOrg) {
                self.WB_PolicyCubeProcess.WB_SalesOrgRow = salesOrg
                self.WB_PolicyCubeProcess.cif.WB_SALESORG_ID = wbSalesOrg
            }
        }
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        let mp = this.ValidateMessageProvider
        let pp = this.ValidateProgressProvider
        let cp = self.WB_PolicyCubeProcess.cif

        mp.clearMessages();
        pp.start(`Validating  ${cp.PROCESS_NAME}(${cp.PROCESS_ID})`)

        self.ValidateOption = options || {};

        try {
            if (_.isEmpty(self.dbContext)) {
                const sError = 'Unable to determine dbContext to validate Cube Process.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            if (_.isEmpty(cp.PROCESS_ID))
                ErrorMessage_restate(mp, true, 'EmptyId', {Title: "Cube Process Id is Empty", Desc:""})

            if (_.isEmpty(cp.PROCESS_NAME))
                ErrorMessage_restate(mp, true, 'EmptyName', {Title: "Cube Process Name is Empty", Desc:""})

            if(cp.WB_ENTITY_ID_CUSTOMER_SRC == 0)
                ErrorMessage_restate(mp, true, 'EmptySrcCust', {Title: "Source Customer Type is Empty", Desc:""})

            if(cp.WB_ENTITY_ID_PRODUCT_SRC == 0)
                ErrorMessage_restate(mp, true, 'EmptySrcPrd', {Title: "Source Product Type is Empty", Desc:""})

            if(cp.WB_ENTITY_ID_PERIOD_SRC == 0)
                ErrorMessage_restate(mp, true, 'EmptySrcTime', {Title: "Source Time Type is Empty", Desc:""})

            if(cp.WB_ENTITY_ID_CUSTOMER_TGT == 0)
                ErrorMessage_restate(mp, true, 'EmptyTgtCust', {Title: "Target Customer Type is Empty", Desc:""})

            if(cp.WB_ENTITY_ID_PRODUCT_TGT == 0)
                ErrorMessage_restate(mp, true, 'EmptyTgtPrd', {Title: "Target Product Type is Empty", Desc:""})

            if(cp.WB_ENTITY_ID_PERIOD_TGT == 0)
                ErrorMessage_restate(mp, true, 'EmptyTgtTime',{Title:  "Target Time Type is Empty", Desc:""})

            if((cp.WB_ENTITY_ID_CUSTOMER_SRC == cp.WB_ENTITY_ID_CUSTOMER_TGT && cp.WB_ENTITY_ID_CUSTOMER_TGT != 0) &&
                (cp.WB_ENTITY_ID_PRODUCT_SRC == cp.WB_ENTITY_ID_PRODUCT_TGT && cp.WB_ENTITY_ID_PRODUCT_TGT != 0) &&
                (cp.WB_ENTITY_ID_PERIOD_SRC == cp.WB_ENTITY_ID_PERIOD_TGT && cp.WB_ENTITY_ID_PERIOD_TGT != 0)
            )
                ErrorMessage_restate(mp, true, 'SameSrcTgt', {Title: "Source and Target Type selection should be different for at least one dimension", Desc:""})


            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const pcp = self.drWB_PolicyCubeProcess.cif;
        this.SaveProgressProvider.start(
            'Saving ' + pcp.cif.PROCESS_NAME + '(' + pcp.cif.PROCESS_ID + ')'
        );

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Cube Process.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if(!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }

            await saveFunction(esWB_Policy_Cube_Process, self.drWB_PolicyCubeProcess, {})

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {

        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        const pcp = this.drWB_PolicyCubeProcess;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            'Deleting ' + pcp.cif.PROCESS_NAME + '(' + pcp.cif.PROCESS_ID + ')'
        );

        try {
            self.drWB_PolicyCubeProcess.markDeleted();

            if (bSaveNow)
                await self.SaveUsing(false, {bSkipValidations: true})

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            self.DeleteProgressProvider.exception(err);
        }
        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this

        const src: PolicyCubeProcessBO = this
        const tgt = new PolicyCubeProcessBO({})
        await tgt.LoadNew({
            wbMasterDataSetId: src.drWB_PolicyCubeProcess.cif.WB_MDSET_ID,
        })

        if (!tgt.bIsLoaded) {
            throw new Error("Unable to create new Policy Cube Process")
        }

        tgt.WB_PolicyCubeProcess.cif.PROCESS_NAME = ''
        RecordUtils.CopyProperties(tgt.dbContext, esWB_Policy_Cube_Process,
            src.drWB_PolicyCubeProcess, tgt.drWB_PolicyCubeProcess,
            [],
            ['PROCESS_ID', 'PROCESS_NAME', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            {}
        )
        tgt.Identity.setAll(tgt.WB_PolicyCubeProcess.cif.WB_POLICYCUBEPROCESS_ID, tgt.WB_PolicyCubeProcess.cif.PROCESS_ID, tgt.WB_PolicyCubeProcess.cif.PROCESS_NAME)


        return tgt
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_PolicyCubeProcess, opts)
    }


    //</editor-fold>



    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const createOptions: CreateOptions = {
            standardConditions: {
                [esWB_Policy_Cube_Process]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctIncludeBelongsToNamedRelations: { }
        };

        return new ESRecordResolver(dbContext, createOptions);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Policy_Cube_Process, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(PolicyCubeProcessBO, "PolicyCubeProcessBO", "Policy Cube Process")
